// src/components/MultiLineIngressEgressChart.jsx
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

var timeout, current_chart;
var scroll_event_happening = false;
// Solid palette (repeats if you have lots of groups)
const PALETTE = [
  "#3A416F",
  "#17C1E8",
  "#82D616",
  "#FBCF33",
  "#EA0606",
  "#8392AB",
  "#CB0C9F",
  "#FF6F91",
  "#4e79a7",
  "#f28e2b"
];

function pickColor(i) {
  return PALETTE[i % PALETTE.length];
}

/**
 * Supported metrics:
 * - "ingress": plot ingress (positive)
 * - "egress": plot egress (negative, so it appears below 0)
 * - "net": plot net = in - out (can be + or -)
 * - "occupancy": plot occupancy series if provided on each group (preferred for "current people in area")
 *
 * Expected `series` shape:
 * {
 *   [groupKey]: {
 *     ingress: number[],
 *     egress: number[],
 *     occupancy?: number[]   // only required for metric="occupancy"
 *   }
 * }
 */
function toMetricArray(v, metric) {
  if (metric === "occupancy") return (v.occupancy ?? []).map((x) => Number(x ?? 0));

  if (metric === "egress") return (v.egress ?? []).map((x) => -Number(x ?? 0));

  if (metric === "net") {
    return (v.ingress ?? []).map(
      (x, i) => Number(x ?? 0) - Number(v.egress?.[i] ?? 0)
    );
  }

  // default: ingress
  return (v.ingress ?? []).map((x) => Number(x ?? 0));
}

function sumArrays(arrays, length) {
  const out = Array.from({ length }, () => 0);
  for (const a of arrays) {
    for (let i = 0; i < length; i++) out[i] += Number(a[i] ?? 0);
  }
  return out;
}

/**
 * MultiLineIngressEgressChart
 *
 * Props:
 * - labels: string[]
 * - series: { [groupKey]: { ingress: number[], egress: number[], occupancy?: number[] } }
 * - metric: "ingress" | "egress" | "net" | "occupancy"
 * - stacked: boolean (true => stacked solid fills; false => overlapping lines)
 * - showTotal: boolean (adds a dashed Total line = sum of datasets)
 *
 * Notes:
 * - Egress is plotted negative (below 0).
 * - Stacked mode uses SOLID fills (no gradients) to keep comparisons honest.
 * - Overlapping mode uses no fill by default (best for comparison).
 */
export default function MultiLineIngressEgressChart({
  labels = [],
  series = {},
  metric = "ingress",
  title = "",
  stacked = true,
  showTotal = true,
  overlapGradientFill = false, // optional: subtle gradient only when NOT stacked
  onHoverEpoch,
  setStartTime,
  setEndTime,
  startLimit,
  endLimit
}) {
  const entries = Object.entries(series || {});

  if (!labels.length || entries.length === 0) {
    return (
      <Card>
        <SoftBox p={3}>
          <SoftTypography variant="h6">{title}</SoftTypography>
          <SoftTypography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
            No data to display for the current filter/grouping.
          </SoftTypography>
        </SoftBox>
      </Card>
    );
  }

  const groupArrays = entries.map(([_, v]) => toMetricArray(v, metric));
  const total = showTotal ? sumArrays(groupArrays, labels.length) : null;

  const datasets = entries.map(([groupKey, v], i) => {
    const color = pickColor(i);
    const data = toMetricArray(v, metric);

    const backgroundColor =
      stacked
        ? color // ✅ solid fills in stacked mode
        : overlapGradientFill
          ? (context) => {
              const { chart } = context;
              const { ctx, chartArea } = chart;
              if (!chartArea) return "transparent";

              // subtle fade under the line (overlap-only)
              const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              g.addColorStop(0, `rgba(0,0,0,0)`); // keep it very subtle
              g.addColorStop(0.15, `rgba(0,0,0,0)`);
              g.addColorStop(1, `rgba(0,0,0,0)`);
              // If you *do* want a subtle tint, swap the above stops for:
              // g.addColorStop(0, hexToRgba(color, 0.16));
              // g.addColorStop(1, "rgba(0,0,0,0)");
              return g;
            }
          : "transparent";

    return {
      label: groupKey,
      data,

      borderColor: color,
      borderWidth: 2,
      tension: 0.25,
      pointRadius: 0,

      // Fill behaviour
      fill: stacked ? true : overlapGradientFill, // overlap default is false for better comparison
      backgroundColor,

      // Stacking behaviour
      stack: stacked ? "flow" : undefined,

      // Ensure total line draws above these
      order: 1
    };
  });

  if (showTotal && total) {
    datasets.push({
      label: "Total",
      data: total,
      tension: 0.2,
      pointRadius: 0,
      borderWidth: 3,
      borderColor: "#111111",
      backgroundColor: "transparent",
      fill: false,

      borderDash: [8, 6],
      stack: undefined,
      order: 9999
    });
  }

  const data = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const name = ctx.dataset.label || "";
            const y = Number(ctx.parsed.y ?? 0);

            // show positive values for egress in tooltip
            if (metric === "egress") return `${name}: ${Math.abs(y)}`;
            return `${name}: ${y}`;
          }
        }
      }
    },
    scales: {
      x: { stacked: stacked, type: 'linear' },
      y: { stacked: stacked, beginAtZero: false }
    }
  };

  return (
    <Card>
      <SoftBox p={3}>
        <SoftTypography variant="h6">{title}</SoftTypography>
        <SoftBox mt={2} sx={{ height: 380 }}>
          <Line data={data} options={options} />
        </SoftBox>
      </SoftBox>
    </Card>
  );
}
